import * as THREE from "../../../libs/three.js/build/three.module.js";
import {EventDispatcher} from "../../EventDispatcher.js";
import {profileSegments} from "./ProfileOverlays.js";

import {t} from "../../digitization/i18n/index.js";
// Maaling og digitalisering direkte i 2D-profilvinduet (ProfileWindow).
//
//  - "distance": maal afstand langs en raekke punkter (som Maal afstand i 3D)
//  - "point" / "line": registrer et punkt / en linje (som Punkt og Linje i 3D)
//
// Man klikker i profilen (x = afstand langs profilen, z = kote). Et klik snapper til det
// naermeste punktskypunkt, ellers bruges musens egen position paa profilplanet. Resultatet
// lander som en ALMINDELIG registrering/maaling i 3D (Registreringer) i verdenskoordinater,
// og tegnes ogsaa i profilen med labels (laengde, vandret, lodret, haeldning).
// Hoejreklik / Enter afslutter, Esc fortryder, Backspace fjerner sidste punkt.

const TOOL_TO_DIGITIZATION = {
	distance: {tool: "digitize_distance", geometry: "LineString", minPoints: 2},
	point: {tool: "digitize_point", geometry: "Point", minPoints: 1},
	line: {tool: "digitize_line", geometry: "LineString", minPoints: 2},
};

const COLORS = {
	measure: 0xffd400,
	digitize: 0x00e5ff,
};
const CSS_COLORS = {
	measure: "#ffd400",
	digitize: "#00e5ff",
};

const CLICK_MOVE_TOLERANCE_PX = 4;
const SNAP_RADIUS_PX = 12;

// Profil-koordinater (mileage, kote) -> verden [x, y, z] paa profilens lodrette plan
export function profileToWorld(points, mileage, z){
	let segments = profileSegments(points);
	if(segments.length === 0){
		return null;
	}

	let segment = segments[segments.length - 1];
	for(let candidate of segments){
		if(mileage <= candidate.mileage + candidate.length){
			segment = candidate;
			break;
		}
	}

	let t = mileage - segment.mileage; // maa gerne ligge uden for 0..length (forlaengelse af sidste/foerste segment)
	return [
		segment.start.x + segment.dir.x * t,
		segment.start.y + segment.dir.y * t,
		z,
	];
}

// Maal i profilplanet for ét segment: laengde, vandret (mileage), lodret (kote), haeldning i %
export function segmentStats(a, b){
	let horizontal = Math.abs(b.m - a.m);
	let dz = b.z - a.z;
	let length = Math.sqrt(horizontal * horizontal + dz * dz);
	let slope = horizontal > 1e-9 ? (dz / horizontal) * 100 : null;
	return {length, horizontal, dz, slope};
}

function formatSigned(value, digits = 2){
	let text = Math.abs(value).toFixed(digits);
	return (value < 0 ? "−" : "+") + text;
}

export function measureLabel(a, b){
	let s = segmentStats(a, b);
	let lines = [`${s.length.toFixed(2)} m`, `↔ ${s.horizontal.toFixed(2)}  ↕ ${formatSigned(s.dz)}`];
	if(s.slope !== null){
		lines.push(`${formatSigned(s.slope, 1)} %`);
	}
	return lines.join("  ");
}

export class ProfileTools extends EventDispatcher{

	constructor(viewer, profileWindow, controller){
		super();

		this.viewer = viewer;
		this.profileWindow = profileWindow;
		this.controller = controller;

		this.tool = null;
		this.current = [];     // punkter i den igangvaerende maaling/tegning
		this.hover = null;     // punkt under musen (snappet eller fri)
		this.finished = [];    // faerdige annotationer: {kind, pts: [{m, z}], labels: [{m, z, text}]}
		this.labels = [];      // aktive label-elementer: {el, m, z}

		this.group = new THREE.Group();
		this.group.name = "profile_tools";
		this.lineMaterial = new THREE.LineBasicMaterial({vertexColors: true, depthTest: false, depthWrite: false});
		this.pointMaterial = new THREE.PointsMaterial({vertexColors: true, size: 7, sizeAttenuation: false, depthTest: false, depthWrite: false});

		this.initDom();
		this.initListeners();
	}

	// --- offentlig API -------------------------------------------------------

	setTool(name){
		if(name !== null && !TOOL_TO_DIGITIZATION[name]){
			return;
		}
		if(name === this.tool){
			return;
		}
		this.cancel();
		this.tool = name;
		this.renderArea.style.cursor = name ? "crosshair" : "";
		this.dispatchEvent({type: "tool_changed", tool: name});
		this.rebuild();
	}

	// nulstiller alt (ny profil): fortryder igangvaerende og fjerner faerdige annotationer
	reset(){
		this.current = [];
		this.hover = null;
		this.finished = [];
		this.rebuild();
		this.dispatchEvent({type: "annotations_changed", count: 0});
	}

	clearAnnotations(){
		this.finished = [];
		this.rebuild();
		this.dispatchEvent({type: "annotations_changed", count: 0});
	}

	cancel(){
		this.current = [];
		this.hover = null;
		this.rebuild();
	}

	// --- opsaetning -----------------------------------------------------------

	initDom(){
		let area = this.profileWindow.renderArea[0];
		this.renderArea = area;

		this.labelLayer = document.createElement("div");
		this.labelLayer.style.cssText = "position:absolute;left:0;top:0;right:0;bottom:0;overflow:hidden;pointer-events:none;z-index:5;";
		area.appendChild(this.labelLayer);

		this.lines = new THREE.LineSegments(new THREE.BufferGeometry(), this.lineMaterial);
		this.markers = new THREE.Points(new THREE.BufferGeometry(), this.pointMaterial);
		this.lines.frustumCulled = false;
		this.markers.frustumCulled = false;
		this.lines.renderOrder = 20;
		this.markers.renderOrder = 20;
		this.group.add(this.lines);
		this.group.add(this.markers);
		this.profileWindow.scene.add(this.group);
	}

	initListeners(){
		let area = this.renderArea;
		let downAt = null;

		area.addEventListener("mousedown", (e) => {
			downAt = {x: e.clientX, y: e.clientY, button: e.button};
		}, true);

		area.addEventListener("mouseup", (e) => {
			if(!this.tool || !downAt || downAt.button !== 0 || e.button !== 0){
				downAt = null;
				return;
			}
			let moved = Math.hypot(e.clientX - downAt.x, e.clientY - downAt.y);
			downAt = null;
			if(moved > CLICK_MOVE_TOLERANCE_PX){
				return; // en panorering, ikke et klik
			}
			this.addPoint(this.resolvePoint(e));
		}, true);

		area.addEventListener("mousemove", (e) => {
			if(!this.tool || this.current.length === 0){
				return;
			}
			this.hover = this.resolvePoint(e);
			this.rebuild();
		}, true);

		area.addEventListener("contextmenu", (e) => {
			if(!this.tool){
				return;
			}
			e.preventDefault();
			e.stopPropagation();
			if(this.current.length > 0){
				this.finish();
			}
		}, true);

		window.addEventListener("keydown", (e) => {
			if(!this.tool){
				return;
			}
			if(e.key === "Escape"){
				if(this.current.length > 0){
					this.cancel();
				}else{
					this.setTool(null);
				}
			}else if(e.key === "Enter"){
				this.finish();
			}else if(e.key === "Backspace" && this.current.length > 0){
				this.current.pop();
				this.rebuild();
			}
		});

		this.profileWindow.addEventListener("rendered", () => this.layoutLabels());
	}

	// --- koordinater ----------------------------------------------------------

	scaleInfo(){
		let w = this.renderArea.clientWidth;
		let h = this.renderArea.clientHeight;
		let cam = this.profileWindow.camera;
		let scale = this.profileWindow.scale;
		return {w, h, cam, scale};
	}

	toProfile(px, py){
		let {w, h, cam, scale} = this.scaleInfo();
		return {m: cam.position.x + (px - w / 2) / scale.x, z: cam.position.z - (py - h / 2) / scale.y};
	}

	toPixel(m, z){
		let {w, h, cam, scale} = this.scaleInfo();
		return {x: w / 2 + (m - cam.position.x) * scale.x, y: h / 2 - (z - cam.position.z) * scale.y};
	}

	// musens punkt: snappet til naermeste punktskypunkt, ellers fri position paa profilplanet
	resolvePoint(e){
		let rect = this.renderArea.getBoundingClientRect();
		let px = e.clientX - rect.left;
		let py = e.clientY - rect.top;
		let free = this.toProfile(px, py);
		let profile = this.controller.profile;
		if(!profile){
			return null;
		}

		let scale = this.profileWindow.scale;
		let radius = SNAP_RADIUS_PX / scale.x;
		let closest = this.profileWindow.pointclouds.size > 0 ? this.profileWindow.selectPoint(free.m, free.z, radius) : null;
		if(closest){
			let p = closest.point;
			let pc = closest.pointcloud;
			let world = [
				p.position[0] + pc.position.x,
				p.position[1] + pc.position.y,
				p.position[2] + pc.position.z,
			];
			return {m: p.mileage, z: world[2], world, snapped: true};
		}

		let world = profileToWorld(profile.points, free.m, free.z);
		return world ? {m: free.m, z: free.z, world, snapped: false} : null;
	}

	// --- indtastning ------------------------------------------------------------

	addPoint(point){
		if(!point || !this.tool){
			return;
		}
		this.current.push(point);
		this.hover = null;

		if(TOOL_TO_DIGITIZATION[this.tool].geometry === "Point"){
			this.finish();
			return;
		}
		this.rebuild();
	}

	finish(){
		let spec = this.tool ? TOOL_TO_DIGITIZATION[this.tool] : null;
		if(!spec || this.current.length < spec.minPoints){
			return;
		}

		let points = this.current;
		let kind = this.tool === "distance" ? "measure" : "digitize";

		let dm = this.viewer.digitizationModule;
		if(dm && dm.addUserFeature){
			let coordinates = points.map(p => p.world);
			dm.addUserFeature({
				geometry: spec.geometry === "Point" ? {type: "Point", coordinates: coordinates[0]} : {type: "LineString", coordinates},
				tool: spec.tool,
			});
		}else if(this.viewer.postMessage){
			this.viewer.postMessage(t("Registrering er ikke tilgængelig - kun profiltegningen vises."), {duration: 3000});
		}

		this.finished.push({kind, pts: points.map(p => ({m: p.m, z: p.z})), labels: this.labelsFor(this.tool, points)});
		this.current = [];
		this.hover = null;
		this.rebuild();
		this.dispatchEvent({type: "annotations_changed", count: this.finished.length});
	}

	// --- tegning ----------------------------------------------------------------

	labelsFor(tool, pts){
		let labels = [];
		if(tool === "distance"){
			let total = 0;
			for(let i = 1; i < pts.length; i++){
				let a = pts[i - 1];
				let b = pts[i];
				total += segmentStats(a, b).length;
				labels.push({m: (a.m + b.m) / 2, z: (a.z + b.z) / 2, text: measureLabel(a, b)});
			}
			if(pts.length > 2){
				let last = pts[pts.length - 1];
				labels.push({m: last.m, z: last.z, text: `Σ ${total.toFixed(2)} m`, offset: 16});
			}
		}else if(tool === "point" && pts.length > 0){
			labels.push({m: pts[0].m, z: pts[0].z, text: t("Kote {0}", pts[0].z.toFixed(2))});
		}
		return labels;
	}

	rebuild(){
		let linePositions = [];
		let lineColors = [];
		let markerPositions = [];
		let markerColors = [];
		let labels = [];

		let rgb = (hex) => [((hex >> 16) & 255) / 255, ((hex >> 8) & 255) / 255, (hex & 255) / 255];
		let pushLine = (a, b, hex) => {
			linePositions.push(a.m, 0, a.z, b.m, 0, b.z);
			lineColors.push(...rgb(hex), ...rgb(hex));
		};
		let pushMarkers = (pts, hex) => {
			for(let p of pts){
				markerPositions.push(p.m, 0, p.z);
				markerColors.push(...rgb(hex));
			}
		};

		for(let annotation of this.finished){
			for(let i = 1; i < annotation.pts.length; i++){
				pushLine(annotation.pts[i - 1], annotation.pts[i], COLORS[annotation.kind]);
			}
			pushMarkers(annotation.pts, COLORS[annotation.kind]);
			for(let label of annotation.labels){
				labels.push({...label, color: CSS_COLORS[annotation.kind]});
			}
		}

		if(this.tool && this.current.length > 0){
			let pts = this.hover ? [...this.current, this.hover] : this.current;
			let hex = this.tool === "distance" ? COLORS.measure : COLORS.digitize;
			for(let i = 1; i < pts.length; i++){
				pushLine(pts[i - 1], pts[i], hex);
			}
			pushMarkers(this.current, hex);
			let color = this.tool === "distance" ? CSS_COLORS.measure : CSS_COLORS.digitize;
			for(let label of this.labelsFor(this.tool, pts)){
				labels.push({...label, color});
			}
		}

		this.setGeometry(this.lines, linePositions, lineColors);
		this.setGeometry(this.markers, markerPositions, markerColors);

		this.labelLayer.innerHTML = "";
		this.labels = labels.map((label) => {
			let el = document.createElement("div");
			el.textContent = label.text;
			el.style.cssText = `position:absolute;white-space:nowrap;font:11px sans-serif;color:#fff;background:rgba(0,0,0,0.72);border-left:3px solid ${label.color};padding:1px 5px;border-radius:2px;`;
			this.labelLayer.appendChild(el);
			return {el, m: label.m, z: label.z, offset: label.offset || 0};
		});

		this.profileWindow.render();
	}

	setGeometry(object, positions, colors){
		let geometry = new THREE.BufferGeometry();
		geometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(positions), 3));
		geometry.setAttribute("color", new THREE.BufferAttribute(new Float32Array(colors), 3));
		object.geometry.dispose();
		object.geometry = geometry;
		object.visible = positions.length > 0;
	}

	layoutLabels(){
		for(let label of this.labels){
			let p = this.toPixel(label.m, label.z);
			label.el.style.left = `${p.x}px`;
			label.el.style.top = `${p.y - 14 - label.offset}px`;
			label.el.style.transform = "translate(-50%, -100%)";
		}
	}
}
